import React, { useEffect } from 'react';
import '@/App.css';
import { GameProvider, useGame } from '@/context/GameContext';
import CRTOverlay from '@/components/CRTOverlay';
import AchievementToasts from '@/components/AchievementToasts';
import StreamerOverlay from '@/components/StreamerOverlay';
import PartyRaceHUD from '@/components/PartyRaceHUD';
import PartyResults from '@/components/PartyResults';
import HotkeyCheatSheet from '@/components/HotkeyCheatSheet';
import RunStatsHUD from '@/components/RunStatsHUD';
import AutoSaveToast from '@/components/AutoSaveToast';
import OnboardingHints from '@/components/OnboardingHints';
import BugReporter from '@/components/BugReporter';
import Menu from '@/pages/Menu';
import MapScreen from '@/pages/MapScreen';
import Combat from '@/pages/Combat';
import Reward from '@/pages/Reward';
import Shop from '@/pages/Shop';
import Rest from '@/pages/Rest';
import EventScreen from '@/pages/EventScreen';
import GameOver from '@/pages/GameOver';
import Leaderboard from '@/pages/Leaderboard';
import DeckView from '@/pages/DeckView';
import HowToPlay from '@/pages/HowToPlay';
import ClassSelect from '@/pages/ClassSelect';
import CardUpgrade from '@/pages/CardUpgrade';
import CardRemove from '@/pages/CardRemove';
import DailyChallenge from '@/pages/DailyChallenge';
import Weekly from '@/pages/Weekly';
import Profile from '@/pages/Profile';
import Draft from '@/pages/Draft';
import Party from '@/pages/Party';
import Marketplace from '@/pages/Marketplace';
import Bounties from '@/pages/Bounties';
import Boon from '@/pages/Boon';
import Shrine from '@/pages/Shrine';
import Treasure from '@/pages/Treasure';
import UpgradePick from '@/pages/UpgradePick';
import Codex from '@/pages/Codex';
import Mastery from '@/pages/Mastery';
import Settings from '@/pages/Settings';
import Achievements from '@/pages/Achievements';
import Credits from '@/pages/Credits';
import AvatarEditor from '@/pages/AvatarEditor';
import BlessingPick from '@/pages/BlessingPick';
import EliteChoice from '@/pages/EliteChoice';
import CombatRecap from '@/pages/CombatRecap';
import WeeklyBrawl from '@/pages/WeeklyBrawl';
import HallOfChampions from '@/pages/HallOfChampions';
import Perks from '@/pages/Perks';

function Router() {
  const { screen } = useGame();
  switch (screen) {
    case 'menu': return <Menu />;
    case 'classselect': return <ClassSelect />;
    case 'daily': return <DailyChallenge />;
    case 'weekly': return <Weekly />;
    case 'profile': return <Profile />;
    case 'draft': return <Draft />;
    case 'party': return <Party />;
    case 'marketplace': return <Marketplace />;
    case 'bounties': return <Bounties />;
    case 'codex': return <Codex />;
    case 'mastery': return <Mastery />;
    case 'settings': return <Settings />;
    case 'achievements': return <Achievements />;
    case 'credits': return <Credits />;
    case 'map': return <MapScreen />;
    case 'combat': return <Combat />;
    case 'reward': return <Reward />;
    case 'shop': return <Shop />;
    case 'rest': return <Rest />;
    case 'cardupgrade': return <CardUpgrade />;
    case 'cardremove': return <CardRemove />;
    case 'event': return <EventScreen />;
    case 'boon': return <Boon />;
    case 'shrine': return <Shrine />;
    case 'treasure': return <Treasure />;
    case 'branchpick': return <UpgradePick />;
    case 'gameover': return <GameOver />;
    case 'leaderboard': return <Leaderboard />;
    case 'deckview': return <DeckView />;
    case 'howto': return <HowToPlay />;
    case 'avatar': return <AvatarEditor />;
    case 'blessing': return <BlessingPick />;
    case 'elite-choice': return <EliteChoice />;
    case 'recap': return <CombatRecap />;
    case 'weeklybrawl': return <WeeklyBrawl />;
    case 'hall': return <HallOfChampions />;
    case 'perks': return <Perks />;
    default: return <Menu />;
  }
}

export default function App() {
  useEffect(() => {
    // v1.0: apply saved accessibility settings on load
    try {
      if (localStorage.getItem('friendslop.reduceMotion') === '1') document.body.classList.add('reduce-motion');
      if (localStorage.getItem('friendslop.shake') === '0') document.body.classList.add('no-shake');
      // v1.9: apply animation speed on boot
      const spd = localStorage.getItem('friendslop.animSpeed') || '1';
      document.body.classList.add(`speed-${spd === '1' ? '1' : spd === '2' ? '2' : 'instant'}`);
    } catch (e) {}
    // v3.17 STEAM: initialize Steamworks bridge if running in Electron. No-op in web.
    import('@/game/steam').then(m => m.initSteam && m.initSteam()).catch(() => {});
    // v3.17: restore persisted fullscreen preference (Electron/desktop chrome only).
    try {
      if (localStorage.getItem('friendslop.fullscreen') === '1' && document.documentElement.requestFullscreen) {
        // Note: browsers require a user gesture — attempt once and swallow rejection.
        document.documentElement.requestFullscreen().catch(() => {});
      }
    } catch (e) {}
  }, []);
  return (
    <div className="App min-h-screen text-white">
      <GameProvider>
        <CRTOverlay />
        <AchievementToasts />
        <StreamerOverlay />
        <PartyRaceHUD />
        <PartyResults />
        <HotkeyCheatSheet />
        <RunStatsHUD />
        <AutoSaveToast />
        <OnboardingHints />
        <BugReporter />
        <Router />
      </GameProvider>
    </div>
  );
}
